import { Badge } from "@/components/ui/badge"

export type CompetitorChangeItem = {
  id: number
  change_type: string
  url: string | null
  details: Record<string, unknown>
  created_at: string
  competitor?: { id: number; name: string }
}

const TYPE_LABELS: Record<string, { label: string; tone: "accent" | "neutral" | "signal" }> = {
  baseline_captured: { label: "Baseline", tone: "neutral" },
  page_added: { label: "New page", tone: "accent" },
  page_removed: { label: "Page removed", tone: "signal" },
  page_updated: { label: "Page updated", tone: "accent" },
  scan_failed: { label: "Scan failed", tone: "signal" },
}

function formatDate(iso: string) {
  return new Date(iso).toLocaleString("en-GB", {
    day: "numeric",
    month: "short",
    hour: "2-digit",
    minute: "2-digit",
  })
}

function pair(value: unknown): [string, string] | null {
  if (Array.isArray(value) && value.length === 2) return [String(value[0] ?? "—"), String(value[1] ?? "—")]
  return null
}

function ChangeDetails({ change }: { change: CompetitorChangeItem }) {
  const d = change.details

  switch (change.change_type) {
    case "baseline_captured":
      return <p className="text-sm text-ink-muted">First scan captured {String(d.pages)} pages as the baseline.</p>
    case "page_added":
      return <p className="text-sm text-ink-body">{String(d.title || change.url)}</p>
    case "page_removed":
      return <p className="text-sm text-ink-body">{String(d.title || change.url)} is no longer reachable.</p>
    case "scan_failed":
      return <p className="text-sm text-ink-muted">{String(d.error)}</p>
    case "page_updated": {
      const title = pair(d.title)
      const description = pair(d.description)
      const words = pair(d.word_count)
      const added = Array.isArray(d.headings_added) ? (d.headings_added as string[]) : []
      const removed = Array.isArray(d.headings_removed) ? (d.headings_removed as string[]) : []
      return (
        <div className="space-y-1 text-sm text-ink-body">
          {title && (
            <p>
              Title: <span className="text-ink-muted line-through">{title[0]}</span> → {title[1]}
            </p>
          )}
          {description && <p>Description changed.</p>}
          {added.length > 0 && (
            <p>
              New sections: <strong>{added.join(" · ")}</strong>
            </p>
          )}
          {removed.length > 0 && (
            <p className="text-ink-muted">Removed sections: {removed.join(" · ")}</p>
          )}
          {words && (
            <p className="text-ink-muted">
              Copy rewritten ({words[0]} → {words[1]} words).
            </p>
          )}
        </div>
      )
    }
    default:
      return null
  }
}

export function ChangeFeed({ changes, emptyText }: { changes: CompetitorChangeItem[]; emptyText: string }) {
  if (changes.length === 0) {
    return <p className="px-5 py-8 text-center text-sm text-ink-muted">{emptyText}</p>
  }

  return (
    <ul className="divide-y divide-hairline">
      {changes.map((change) => {
        const type = TYPE_LABELS[change.change_type] ?? { label: change.change_type, tone: "neutral" as const }
        return (
          <li key={change.id} className="px-5 py-3">
            <div className="flex flex-wrap items-center gap-2">
              <Badge tone={type.tone}>{type.label}</Badge>
              {change.competitor && (
                <span className="text-sm font-medium text-ink-display">{change.competitor.name}</span>
              )}
              <span className="text-xs text-ink-muted">{formatDate(change.created_at)}</span>
              {change.url && (
                <a
                  href={change.url}
                  target="_blank"
                  rel="noreferrer noopener"
                  className="truncate font-mono text-xs"
                >
                  {change.url.replace(/^https?:\/\//, "")}
                </a>
              )}
            </div>
            <div className="mt-1">
              <ChangeDetails change={change} />
            </div>
          </li>
        )
      })}
    </ul>
  )
}
